import { BreadcrumbSwitcherMenu } from '@portal/core/components/breadcrumb-switcher-menu/BreadcrumbSwitcherMenu'
import graphql from 'babel-plugin-relay/macro'
import paths from 'paths'
import { Suspense, useContext, useEffect, useState } from 'react'
import { useLazyLoadQuery, useRefetchableFragment } from 'react-relay/hooks'
import { useNavigate } from 'react-router-dom'

import AppContext from '../../context'
import { TenantActionMenu_viewer$key } from './__generated__/TenantActionMenu_viewer.graphql'
import { TenantActionMenuQuery } from './__generated__/TenantActionMenuQuery.graphql'
import { TenantActionMenuRefetchQuery } from './__generated__/TenantActionMenuRefetchQuery.graphql'

const query = graphql`
  query TenantActionMenuQuery {
    viewer {
      ...TenantActionMenu_viewer
    }
  }
`

const refetchableOrganizations = graphql`
  fragment TenantActionMenu_viewer on Viewer
  @refetchable(queryName: "TenantActionMenuRefetchQuery") {
    me {
      id
      apps {
        # eslint-disable-next-line relay/unused-fields
        id
      }
      organizations {
        id
        name
        slug
      }
    }
    organizations {
      totalCount
    }
  }
`

type Props = {
  /** The current tenant slug, or null for Personal */
  currentTenant: string | null
  /** The display text for the current tenant */
  currentTenantName: string
  /** Callback for when the tenant is changed */
  onChangeTenant: (tenant: string | null) => void
}

/**
 * An action menu for switching between organizations in breadcrumbs.
 * Shows the current organization with a dropdown to switch to others.
 */
export default function TenantActionMenu({
  currentTenant,
  currentTenantName,
  onChangeTenant,
}: Props) {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Suspense
      fallback={
        <span className="text-foreground-subtle text-sm">
          {currentTenantName}
        </span>
      }
    >
      <TenantActionMenuContent
        currentTenant={currentTenant}
        currentTenantName={currentTenantName}
        onChangeTenant={onChangeTenant}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        navigate={navigate}
      />
    </Suspense>
  )
}

function TenantActionMenuContent({
  currentTenant,
  currentTenantName,
  onChangeTenant,
  isOpen,
  setIsOpen,
  navigate,
}: Props & {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  navigate: ReturnType<typeof useNavigate>
}) {
  const { viewer } = useLazyLoadQuery<TenantActionMenuQuery>(query, {})
  const [data, refetch] = useRefetchableFragment<
    TenantActionMenuRefetchQuery,
    TenantActionMenu_viewer$key
  >(refetchableOrganizations, viewer)

  const { isSuperAdmin } = useContext(AppContext)

  useEffect(() => {
    if (isOpen) {
      refetch({}, { fetchPolicy: 'store-and-network' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const organizations = data?.me?.organizations ?? []

  // Gate "New Organization" the same way SideNavUserMenu does: visible to
  // staff, or to any viewer with at least one app or organization. Keeps the
  // two creation entry points consistent.
  const canCreateOrganization =
    !!isSuperAdmin ||
    !!data?.me?.apps.length ||
    !!data?.organizations.totalCount

  const tenantItems = [
    {
      id: 'personal',
      label: 'Personal',
      onClick: () => {
        onChangeTenant(null)
        setIsOpen(false)
      },
      active: currentTenant === null,
    },
    ...organizations.map((org) => ({
      id: org?.id ?? '',
      label: org?.name ?? '',
      onClick: () => {
        onChangeTenant(org?.slug ?? null)
        setIsOpen(false)
      },
      active: currentTenant === org?.slug,
    })),
  ]

  return (
    <BreadcrumbSwitcherMenu
      currentLabel={currentTenantName}
      items={tenantItems}
      footerItem={
        canCreateOrganization
          ? {
              label: 'New Organization',
              onClick: () => {
                navigate(paths.createOrganization())
                setIsOpen(false)
              },
            }
          : undefined
      }
      onOpenChange={setIsOpen}
    />
  )
}
